/**
 * Settings → Language.
 *
 * The list offered and the save behind a pick both come from
 * `useLanguagePreference`, shared with the header switch so the two controls
 * cannot drift apart again. German and English always; French, Arabic,
 * Ukrainian and Spanish for any signed-in account.
 */
import { Globe, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLanguagePreference } from "@/hooks/useLanguagePreference";
import { LANGUAGE_LABELS, coerceLanguage, dirFor } from "@/lib/i18n/languages";

export function LanguagePreferenceCard() {
  const { t } = useLanguage();
  // `true`: this card is the screen the user came to in order to change the
  // language, so confirming the save is the point. The header switch, on the
  // same hook, stays quiet.
  const { language, options, choose, saving } = useLanguagePreference(true);

  return (
    <Card className="rounded-2xl border border-border/60 shadow-sm">
      <div className="p-5 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-foreground">
              {t("settings.language.title", "Language")}
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t(
                "settings.language.desc",
                "Choose the language coliviq uses for you."
              )}
            </p>
          </div>
        </div>

        <div className="space-y-2 max-w-xs">
          <Label htmlFor="settings-language" className="text-sm font-medium">
            {t("settings.language.preferred", "Preferred language")}
          </Label>
          <Select
            value={language}
            onValueChange={(v) => void choose(coerceLanguage(v, language))}
            disabled={saving}
          >
            <SelectTrigger id="settings-language" className="h-11 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {options.map((code) => (
                <SelectItem
                  key={code}
                  value={code}
                  lang={code}
                  dir={dirFor(code)}
                >
                  {LANGUAGE_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                {t("settings.saving", "Saving…")}
              </>
            ) : (
              t(
                "settings.language.hint",
                "Applies straight away, on this and your other devices."
              )
            )}
          </p>
        </div>
      </div>
    </Card>
  );
}

export default LanguagePreferenceCard;
